'use client';

// Seen/progress state lives per-user in Firestore (userTours/{userId}) — see
// src/stores/useUserToursStore.ts. This module is only the in-session bus.

/**
 * Action bus for the guided tours: vault surfaces emit an action when the user
 * actually performs it, and an action-gated tour step advances itself. Fire and
 * forget — emitting with no tour running is a no-op.
 */
/** 'vault-placed' = a whole vault or group chip landed on the board (vs a
 *  single item). 'vault-created' = the create-vault demo finished making the
 *  demo vault. 'demo-submit' = the tooltip's primary button on a
 *  submit-gated demo step (the typing demo waits for it before ingesting).
 *  'node-saved' is reserved for a future save-to-vault step. */
export type GuidedTourAction =
  | 'panel-opened'
  | 'item-added'
  | 'item-placed'
  | 'vault-created'
  | 'vault-placed'
  | 'demo-submit'
  | 'node-saved'
  // Discover Content → Instagram library tour (InstagramLibraryTour.tsx).
  | 'library-platform-instagram'
  | 'library-search-started'
  // The tour taking / releasing the panel — other announcements stand down.
  | 'library-tour-started'
  /** The retake card under the category chips: start the library tour again from the hands-on step. */
  | 'library-tour-retake'
  | 'library-tour-ended'
  // Every step viewed, then the "watch it here" step specifically (the first
  // card keeps its play button up and shows the pointer glove while it lasts).
  | 'library-tour-step'
  | 'library-tour-play-step'
  // The Discover Content panel was opened from the toolbar (button or D).
  | 'discover-panel-opened'
  // The tour reached its "stack a term" step (the header pins a Marketing chip).
  | 'library-tour-stack-step'
  // A refine chip / stacked term was applied under a library search.
  | 'library-refined'
  // Library reels landed on the board (Quick Add or Add Selected) — the tour's
  // finale moves from the button to the group it just created.
  | 'library-posts-added'
  // Discover Content → LinkedIn announcement card (LinkedInSupportSpotlight.tsx).
  | 'discover-platform-linkedin'
  // "Restart tour" from a stuck step or the crash card: the tour that owns the
  // run tears it down and starts over from its first step.
  | 'tour-restart'
  // A library search failed twice in a row while the tour was running: the
  // step it was on may never advance, so the tooltip gets its X back.
  | 'library-search-failed';

/** Name of the vault the create-vault demo spins up FOR the user — the
 *  drag-a-vault step then showcases this exact card. Creating it ourselves
 *  (instead of asking them to click New Vault) keeps the tour anchored: a real
 *  click navigates into the fresh vault and tears out the landing-grid anchor
 *  the next step needs. */
export const VAULT_TOUR_DEMO_VAULT_NAME = 'Swipe Files';
export const VAULT_TOUR_DEMO_VAULT_EMOJI = '🗂️';

/** Just enough of a vault item to draw a recognizable card in the tour demo. */
export type VaultTourItemPreview = {
  title?: string | null;
  platform?: string | null;
  type?: string;
};

const TOUR_ACTION_EVENT = 'poppy-vault-tour-action';

// True while a guided vault tour is mounted. The tour needs the panel to stay
// interactive (drag steps hide the overlay entirely), which leaves controls
// live that tear out the tour's anchors mid-run: closing the panel unmounts
// every target, and the real "New Vault" button navigates into the fresh vault.
// Those surfaces check this flag and stand down; Escape (joyride's dismiss key)
// remains the intentional way out of the tour.
let tourActive = false;
const releaseListeners = new Set<() => void>();

export const setGuidedTourActive = (active: boolean): void => {
  const released = tourActive && !active;
  tourActive = active;
  if (released) releaseListeners.forEach(listener => listener());
};

/** Called whenever the running tour lets go, so a waiting tour can claim the slot. */
export function onGuidedTourReleased(listener: () => void): () => void {
  releaseListeners.add(listener);
  return () => releaseListeners.delete(listener);
}

export const isGuidedTourActive = (): boolean => tourActive;

/**
 * One guided tour at a time. Every tour (vault, creator profile, Instagram
 * library) calls this at the moment it decides to start and stands down on
 * `false`; the winner holds the flag from this call, not from its joyride's
 * mount a dynamic import later, so two tours deciding in the same beat can't
 * both win. Released when the winner's GuidedJoyride unmounts (or by
 * `releaseGuidedTour` if it never gets that far).
 */
export function claimGuidedTour(): boolean {
  if (tourActive) return false;
  tourActive = true;
  return true;
}

export const releaseGuidedTour = (): void => {
  setGuidedTourActive(false);
};

// Whether the running tour handles 'tour-restart' — the tooltip and the crash
// card offer "Restart tour" only then (otherwise just "End tour").
let tourRestartable = false;

export const setGuidedTourRestartable = (restartable: boolean): void => {
  tourRestartable = restartable;
};

export const isGuidedTourRestartable = (): boolean => tourRestartable;

// Set when the thing a step waits on has failed for good (a library search
// that errored twice): every step becomes closable again, X included, so the
// user is never held on a step that cannot advance. Cleared with the tour.
let tourExitForced = false;

export const isGuidedTourExitForced = (): boolean => tourExitForced;

// The most recent item the user added this session — lets the drag-drop demo
// animate THEIR content instead of a generic skeleton card.
let lastAddedItem: VaultTourItemPreview | null = null;

export const getVaultTourLastAddedItem = () => lastAddedItem;

// The library tour's "waiting for a search" phase, kept as state and not only
// as events: the Discover panel usually mounts AFTER the tour started (the
// announcement opens it), so a listener attached then would miss the start
// and never open the categories card the first hands-on step points at.
let libraryTourAwaitingSearch = false;
export const getLibraryTourAwaitingSearch = (): boolean => libraryTourAwaitingSearch;
/** The tour is on "stack a term": the related chips must offer Marketing. */
let libraryTourAwaitingRefine = false;
export const getLibraryTourAwaitingRefine = (): boolean => libraryTourAwaitingRefine;
/** Any library tour run is live (the panel zooms the canvas to a new group for the finale). */
let libraryTourRunning = false;
export const isLibraryTourRunning = (): boolean => libraryTourRunning;

export function emitGuidedTourAction(action: GuidedTourAction, item?: VaultTourItemPreview): void {
  if (typeof window === 'undefined') return;
  if (action === 'item-added' && item) lastAddedItem = item;
  if (action === 'library-tour-started') {
    libraryTourAwaitingSearch = true;
    libraryTourRunning = true;
  }
  if (action === 'library-search-started' || action === 'library-tour-ended') {
    libraryTourAwaitingSearch = false;
  }
  if (action === 'library-tour-stack-step') libraryTourAwaitingRefine = true;
  if (action === 'library-refined' || action === 'library-tour-ended') {
    libraryTourAwaitingRefine = false;
  }
  if (action === 'library-tour-ended') libraryTourRunning = false;
  if (action === 'library-search-failed') tourExitForced = true;
  if (action === 'library-tour-started' || action === 'library-tour-ended') tourExitForced = false;
  try {
    window.dispatchEvent(new CustomEvent(TOUR_ACTION_EVENT, { detail: action }));
  } catch {
    // ignore
  }
}

export function onGuidedTourAction(handler: (action: GuidedTourAction) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<GuidedTourAction>).detail);
  window.addEventListener(TOUR_ACTION_EVENT, listener);
  return () => window.removeEventListener(TOUR_ACTION_EVENT, listener);
}

// ─── Library tour: the group the last add created ─────────────────────────────
// Published by the Discover panel right before it fires `library-posts-added`,
// read by the tour (mounted with the board chrome) to aim its finale at the
// new group node. A tiny external store so the tour re-renders with the id
// before the joyride advances.
let libraryTourAddedGroupId: string | null = null;
const addedGroupListeners = new Set<() => void>();

export const getLibraryTourAddedGroupId = (): string | null => libraryTourAddedGroupId;
export function setLibraryTourAddedGroupId(id: string | null): void {
  libraryTourAddedGroupId = id;
  addedGroupListeners.forEach(listener => listener());
}
export function subscribeLibraryTourAddedGroupId(listener: () => void): () => void {
  addedGroupListeners.add(listener);
  return () => addedGroupListeners.delete(listener);
}
